import { useTranslation } from 'react-i18next';
import { useLanguageLabels } from '~/hooks/useLanguageLabels';
import type { LanguageCode } from '~/types/congregationGroupTypes';
import { formatLongDate } from '~/utils/dateFormat';
import WbsTable, { type Column } from './WbsTable';

export interface StudyHelperHistoryRecord {
  id: string;
  studyHelper: string;
  location: string;
  /**
   * The helper's teaching languages, not their account language — legacy reads
   * xprofile field 12 and comma-joins it
   * (`themes/wbs/modules/datatable/columns/StudyHelperLanguageColumn.php:26-42`).
   */
  languages: LanguageCode[];
  adoptedAt: string;
  releasedAt: string | null;
  releasedBy: string | null;
}

interface StudyHelpersHistoryTableProps {
  studentName: string;
  data: StudyHelperHistoryRecord[];
}

export function StudyHelpersHistoryTable({
  studentName,
  data,
}: StudyHelpersHistoryTableProps) {
  const { t } = useTranslation();
  const languageLabels = useLanguageLabels();
  // Only Adopted At and Released At are sortable: legacy's base column is
  // `$orderable = false` (`modules/datatable/base/Column.php:8`) and this table
  // opts exactly two in (`tables/student-study-helper-history.php:52-53`).
  const columns: Column<StudyHelperHistoryRecord>[] = [
    {
      key: 'studyHelper',
      title: t('lessons.study-helper'),
    },
    {
      key: 'location',
      title: t('wbs.location'),
    },
    {
      key: 'languages',
      // Singular header over a joined list, which is what legacy ships (unit
      // 22240) — deliberately unlike the group-members roster, which uses the
      // plural key.
      title: t('wbs.language'),
      render: (languages) =>
        languages
          .map((language) => languageLabels[language] ?? language)
          .join(', '),
    },
    {
      key: 'adoptedAt',
      title: t('wbs.adopted-at'),
      sortable: true,
      render: (value) => formatLongDate(value),
    },
    {
      key: 'releasedAt',
      title: t('wbs.released-at'),
      sortable: true,
      render: (value) => (value ? formatLongDate(value) : t('wbs.n-a-2')),
    },
    {
      key: 'releasedBy',
      title: t('wbs.released-by'),
      // Empty is normal, not a join bug: 184 of the 227 released rows in the
      // migrated set carry no releaser at all.
      render: (value) => value || t('wbs.n-a-2'),
    },
  ];

  // Divergence §8.9: legacy renders a search box and two free-text filters here
  // and this table renders neither, because `WbsTable` applies neither in client
  // mode (`WbsTable.tsx:328,417-425,479-500`) — an inert control is worse than
  // an absent one. `showSettings` stays; column visibility IS real client-side.
  return (
    <WbsTable
      title={studentName}
      data={data}
      columns={columns}
      showSettings
      cellStyle={{ padding: '10px 8px' }}
    />
  );
}
